import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "./components/ErrorBoundary";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./locale/i18n";
import { detectStrategy, loadPlatformBootstrapProps, } from "./mounting/definitions";
import detectCurrentPlatform from "./mounting/detectCurrentPlatform";
import console from "./log";
import { definitions } from "./mounting/definitions";
import { getShopifyShopName, getWcShopName, unmountAllFlows } from "./helpers";
import { setCheckIdInLocalStorage, setCheckVerificationStatusInLocalStorage, } from "./mounting/precheckout";
import { resetCart, getCart } from "./utilities/shopify";
console.debug("Real ID :: JS SDK loaded");
async function setupEnvironment() {
    const platform = detectCurrentPlatform();
    console.debug(`Real ID :: detected platform :: ${platform}`);
    console.debug(`Real ID :: environment :: ${process.env.NODE_ENV}`);
    const platformBootstrapProps = await loadPlatformBootstrapProps(platform);
    console.debug({ platformBootstrapProps });
    const strategy = detectStrategy(platform, platformBootstrapProps, initialize);
    if (!strategy) {
        new Error("Real ID :: No matching strategy found");
    }
    const { entrySelectors, onIdCheckError, onIdCheckSuccess, onIdCheckFail, onIdCheckCreate, bootstrap, } = strategy;
    const props = await bootstrap();
    const mountEls = strategy.createMount({ entrySelectors, props, initialize });
    const additionalProps = await strategy.getAdditionalEnvironmentProps();
    const environment = {
        strategy: strategy.strategy,
        platform,
        onIdCheckError,
        onIdCheckSuccess,
        onIdCheckFail,
        onIdCheckCreate,
        roots: [],
        removeRoots: () => {
            (environment.roots || [])?.forEach((root) => {
                root.unmount();
            });
            environment.roots = [];
        },
        // used only for automated ID checks with Vouched
        //  if the ID check is manual, then the result is always "in_review" because the merchant needs to manually review it
        onIdCheckComplete: ({ env, precheckoutDone, setCheck, check, staleCheck, done, }) => {
            const newCheck = {
                ...staleCheck,
                ...{ step: check?.step },
                ...{ job: check?.job },
            };
            setCheck(newCheck);
            console.debug(`Real ID :: onIdCheckComplete`);
            // user has failed their ID check
            if (check?.step === "in_review" || !check?.job?.result?.success) {
                env.onIdCheckError?.("results_in_review");
                const failedCheckEvent = new CustomEvent("real-id-check-failed", {
                    detail: {
                        check: newCheck,
                    },
                });
                try {
                    console.debug("Real ID :: emitting real-id-check-failed event");
                    window?.dispatchEvent(failedCheckEvent);
                }
                catch (e) {
                    console.debug("Real ID :: failed to emit real-id-check-failed event");
                    console.error(e);
                }
                done();
                return;
            }
            // user has successfully completed an ID check
            if (check?.step === "completed" && check?.job?.result?.success) {
                console.debug(`Real ID :: check ${check?.id} :: completed successfully`);
                env.onIdCheckSuccess(check);
                if (precheckoutDone?.setDone) {
                    precheckoutDone.setDone(true);
                }
                const passedCheckEvent = new CustomEvent("real-id-check-passed", {
                    detail: {
                        check: newCheck,
                    },
                });
                try {
                    console.debug("Real ID :: emitting real-id-check-passed event");
                    window?.dispatchEvent(passedCheckEvent);
                }
                catch (e) {
                    console.debug("Real ID :: failed to emit real-id-passed-check event");
                    console.error(e);
                }
                done();
            }
            // either way, pass, fail or in_review - move to the "done" step to show the final screen
            console.debug(`Real ID :: onCheckComplete :: updating check to`, {
                ...staleCheck,
                ...{ job: check?.job },
            });
        },
        ...(additionalProps || {}),
        mountEls,
    };
    console.debug(`Real ID :: initialized environment`);
    console.debug(environment);
    return { environment, mountEls, strategy };
}
function ErrorFallback() {
    const { t } = useTranslation();
    return (_jsx("div", { role: "alert", children: _jsx("p", { children: t("components.App.error_fallback") }) }));
}
const fallback = _jsx(ErrorFallback, {});
function mountFlow({ environment, mountEls, strategy, mode = "full", }) {
    console.debug(`Real ID :: initialized environment`);
    console.table(environment);
    // Set logger context for CloudWatch logging
    console.setContext({
        platform: environment?.platform,
        strategy: environment?.strategy,
        shopName: environment?.shopName,
        checkId: environment?.checkId,
    });
    if (mountEls) {
        const roots = mountEls.map((mountEl) => {
            console.debug(`Mounting Real ID to `, mountEl);
            const AppComponent = strategy.rootComponent(mode);
            const root = createRoot(mountEl);
            root.render(_jsx(Suspense, { fallback: _jsx("div", {}), children: _jsx(ErrorBoundary, { fallback: fallback, children: _jsx(AppComponent, { environment: environment }) }) }));
            return root;
        });
        // apply the real-id-mounted=true attribute to all entry selectors for the strategy
        // we're doing this after the mounts have been created, so the "real-id-mounted" state is as true as possible
        const entryEls = document.querySelectorAll(strategy.entrySelectors.join(", "));
        [...entryEls].map((el) => {
            el.setAttribute("real-id-mounted", "true");
        });
        environment.roots = roots;
        window.realIdManager.roots = roots;
    }
}
function initialize() {
    console.log("initializing default flow");
    if (!window.realIdManager) {
        window.realIdManager = { roots: [] };
    }
    setupEnvironment()
        .then(({ environment, mountEls, strategy, }) => {
        mountFlow({ environment, mountEls, strategy });
    })
        .catch((e) => console.debug(e));
}
initialize();
window.addEventListener("real-id-initialize", () => {
    console.log("real-id-intialize received");
    initialize();
});
window.addEventListener("updated.ecomify.cart", () => {
    console.log("updated.ecomify.cart received");
    initialize();
});
/**
 * List of side-cart elements that will emit the real-id-initialize event
 *
 * @TODO: add more elements to the list as they are discovered
 */
const eventEmitEls = document.querySelectorAll('.real-id-initialize, .js-go-cart-trigger, .js-go-cart-add-to-cart, [data-btn-addToCart], [data-btn-addtocart], [data-cart-sidebar], [data-add-to-cart-text], [data-action="increment"], .shopify-installments, [data-open-quick-view-popup], .quickview-icon, .modem-main, .qikify-stickycart-v2, [data-section="kadence_customizer_cart"]');
if (eventEmitEls && eventEmitEls.length > 0) {
    eventEmitEls.forEach((eventEmitEl) => {
        console.debug(`Real ID :: registering initialize event emitter`);
        eventEmitEl.addEventListener("click", () => {
            console.debug("Real ID :: emit real-id-intialize event");
            const btnClick = new CustomEvent("real-id-initialize");
            window.dispatchEvent(btnClick);
        });
    });
}
/**
 * WooCommerce Checkout Blocks integration
 *
 * WooCommerce will emit @wordpress/hook events on specific checkout events, like when the form is loaded.
 * If the normal initization beats the client side render, then listen for the specific checkout render finished event
 *
 * List of WC events:
 * https://github.com/woocommerce/woocommerce/blob/trunk/plugins/woocommerce-blocks/docs/internal-developers/blocks/feature-flags-and-experimental-interfaces.md#usages-of-experimental-prefix
 *
 * @wordpress/hooks docs:
 * https://developer.wordpress.org/block-editor/reference-guides/packages/packages-hooks/
 */
if (window?.wp?.hooks) {
    console.debug("WC :: block usage detected");
    const hooks = window.wp.hooks;
    hooks.addAction("experimental__woocommerce_blocks-checkout-render-checkout-form", "realidcheckoutexperimental", () => {
        console.debug("WC :: checkout form rendered");
        // re-initialize flow, now that the form is rendered
        setTimeout(() => {
            initialize();
        }, 100);
    });
    hooks.addAction("woocommerce_blocks-checkout-render-checkout-form", "realidcheckout", () => {
        console.debug("WC :: checkout form rendered");
        // re-initialize flow, now that the form is rendered
        setTimeout(() => {
            initialize();
        }, 100);
    });
}
if (window?.jQuery) {
    console.log("looking for ninjaforms");
    window.jQuery(document).on("nfFormReady", () => {
        console.log("Ninja forms loaded");
        initialize();
    });
}
export function createFlow(options) {
    console.debug("Creating flow manually");
    const strategy = definitions.hosted.filter((strat) => strat.strategy === "sdk")[0];
    if (!options.target) {
        throw new Error("A target is required to mount the ID verification flow to.");
    }
    if (options.mode && !["full", "modal"].includes(options.mode)) {
        throw new Error("Only full or modal modes supported");
    }
    // set checkId in local storage if given as a option param
    if (options?.checkId) {
        setCheckIdInLocalStorage(options.checkId);
    }
    const environment = {
        strategy: strategy.strategy,
        platform: detectCurrentPlatform(),
        onIdCheckError: (error) => {
            // enable WC checkout depending on the error type
            if ((error === "check_already_completed" ||
                error === "verified_customer") &&
                options?.mode === "modal") {
                if (window?.realIdManager?.roots?.length > 0) {
                    const rootCount = window.realIdManager.roots.length;
                    [...window.realIdManager.roots].forEach((root, index) => {
                        console.log(`unmounting root ${index + 1} of ${rootCount}`);
                        root.unmount();
                    });
                    console.log("unmounting all mounts");
                }
            }
        },
        removeRoots: () => {
            (environment.roots || [])?.forEach((root) => {
                root.unmount();
            });
            environment.roots = [];
        },
        onIdCheckCreate: (check) => {
            setCheckIdInLocalStorage(check.id);
        },
        onIdCheckFail: () => { },
        onIdCheckSuccess: (check) => {
            // store IDv success in localstorage
            setCheckVerificationStatusInLocalStorage(check.id, true);
            // seems redundant, but also update the checkId in local storage
            // this is because multiple instances of the flow can be on one page. For instance, if the page has two mountable elements ("Buy it now" and side cart) then two instances of the flow start, and there are two separate ID checks made
            // let the completed check "win"
            setCheckIdInLocalStorage(check.id);
            if (options.mode == "modal") {
                unmountAllFlows();
            }
        },
        // used only for automated ID checks with Vouched
        //  if the ID check is manual, then the result is always "in_review" because the merchant needs to manually review it
        onIdCheckComplete: ({ env, precheckoutDone, setCheck, check, staleCheck, done, }) => {
            const newCheck = {
                ...staleCheck,
                ...{ step: check?.step },
                ...{ job: check?.job },
            };
            setCheck(newCheck);
            console.debug(`Real ID :: onIdCheckComplete`);
            // user has failed their ID check
            if (check?.step === "in_review" || !check?.job?.result?.success) {
                env.onIdCheckError?.("results_in_review");
                const failedCheckEvent = new CustomEvent("real-id-check-failed", {
                    detail: {
                        check: newCheck,
                    },
                });
                try {
                    console.debug("Real ID :: emitting real-id-check-failed event");
                    window?.dispatchEvent(failedCheckEvent);
                }
                catch (e) {
                    console.debug("Real ID :: failed to emit real-id-check-failed event");
                    console.error(e);
                }
                done();
                return;
            }
            // user has successfully completed an ID check
            if (check?.step === "completed" && check?.job?.result?.success) {
                console.debug(`Real ID :: check ${check?.id} :: completed successfully`);
                env.onIdCheckSuccess(check);
                if (precheckoutDone?.setDone) {
                    precheckoutDone.setDone(true);
                }
                const passedCheckEvent = new CustomEvent("real-id-check-passed", {
                    detail: {
                        check: newCheck,
                    },
                });
                try {
                    console.debug("Real ID :: emitting real-id-check-passed event");
                    window?.dispatchEvent(passedCheckEvent);
                }
                catch (e) {
                    console.debug("Real ID :: failed to emit real-id-passed-check event");
                    console.error(e);
                }
                done();
            }
            // either way, pass, fail or in_review - move to the "done" step to show the final screen
            console.debug(`Real ID :: onCheckComplete :: updating check to`, {
                ...staleCheck,
                ...{ job: check?.job },
            });
        },
        // infer the shopName from the window based on the platform, or simply accept it as an option (for now until public keys are a thing)
        shopName: getShopifyShopName() || options.shopName || getWcShopName(),
        shopSettings: {},
        mode: options.mode || "full",
        theme: options?.theme,
        ...(options?.checkId ? { checkId: options.checkId } : {}),
        // Customer data for pre-population
        ...(options?.email ? { customerEmail: options.email } : {}),
        ...(options?.firstName ? { customerFirstName: options.firstName } : {}),
        ...(options?.lastName ? { customerLastName: options.lastName } : {}),
        ...(options?.phone ? { customerPhone: options.phone } : {}),
        // Address data for cross-checking rules
        ...(options?.billingAddress ? { billingAddress: options.billingAddress } : {}),
        ...(options?.shippingAddress ? { shippingAddress: options.shippingAddress } : {}),
    };
    const entrySelectors = options?.target
        ? [options.target]
        : strategy.entrySelectors;
    // hide the entry selectors until verified
    entrySelectors.forEach((selector) => {
        const entry = document.querySelector(selector);
        if (entry) {
            entry.classList.add("hidden-until-verified");
        }
    });
    console.debug({ entrySelectors });
    const mountEls = strategy.createMount({
        entrySelectors,
        initialize,
        props: options,
    });
    console.log(`Created ${mountEls?.length || 0} mount elements`);
    if (!mountEls) {
        console.error(`No mountable elements found with ${options.target}`);
    }
    mountFlow({ environment, mountEls, strategy, mode: options.mode });
}
export const cart = {
    get: () => {
        getCart().then((data) => {
            console.log("Cart data:", data);
            return data;
        });
    },
    reset: () => {
        resetCart().then((data) => {
            console.log("Cart reset:", data);
        });
    },
};
/**
 * Public wrapper for unmounting all flows
 * @returns
 */
export function unmount() {
    return unmountAllFlows();
}
export function customCFIntegration() {
    const startTime = performance.now();
    console.log("Custom CF integration");
    console.log(`[CF] Starting detection, DOM state: ${document.readyState}`);
    console.log(`[CF] Looking for selector: .cf-react-target`);
    let flowLoaded = false;
    // Helper function to extract Customer Fields data with timeout
    const extractCFData = (callback, timeout = 2000) => {
        const startExtractTime = Date.now();
        console.log("[CF] Attempting to extract Customer Fields data...");
        const checkCF = () => {
            // Check if Customer Fields global API is available
            if (window.CF) {
                console.log("[CF] Customer Fields API detected");
                const CF = window.CF;
                CF.ready(() => {
                    CF.customerReady(() => {
                        const $form = document.querySelector("form[data-cf-form]");
                        const cfForm = $form?.cfForm;
                        // Extract data with fallback strategy
                        const email = cfForm?.getFieldValue("email") || CF.customer?.get("email");
                        const firstName = cfForm?.getFieldValue("first_name") || CF.customer?.get("first_name");
                        const lastName = cfForm?.getFieldValue("last_name") || CF.customer?.get("last_name");
                        const phone = cfForm?.getFieldValue("phone") || CF.customer?.get("phone");
                        const extractedData = {
                            email: email || undefined,
                            firstName: firstName || undefined,
                            lastName: lastName || undefined,
                            phone: phone || undefined,
                        };
                        // Log what was found (without PII)
                        const foundFields = Object.entries(extractedData)
                            .filter(([, value]) => value)
                            .map(([key]) => key);
                        if (foundFields.length > 0) {
                            console.log(`[CF] Customer Fields data extracted: ${foundFields.join(", ")}`);
                        }
                        else {
                            console.log("[CF] Customer Fields form found but no data available yet");
                        }
                        callback(extractedData);
                    });
                });
            }
            else if (Date.now() - startExtractTime < timeout) {
                // CF not ready yet, retry
                setTimeout(checkCF, 100);
            }
            else {
                // Timeout reached
                const elapsed = Date.now() - startExtractTime;
                console.log(`[CF] Customer Fields API not available after ${elapsed}ms, proceeding without data`);
                callback(null);
            }
        };
        checkCF();
    };
    // Helper function to check if mount point exists in DOM
    // Note: We intentionally hide the mount with display:none after mounting (SDK creates a sibling),
    // but Customer Fields removes/adds the element from DOM when navigating between steps.
    // So we check DOM presence, not visibility.
    const mountExistsInDOM = () => {
        return !!document.querySelector("#real-id-mount");
    };
    // Helper function to check if Real ID flow is actively being used
    const isFlowActive = () => {
        // Check if flow UI is visible and active
        return !!document.querySelector('.real-id-flow') ||
            !!document.querySelector('[data-testid="real-id-modal"]') ||
            !!document.querySelector('.real-id-modal-overlay');
    };
    // Helper function to unmount flow and reset state
    const unmountFlow = () => {
        console.log("[CF] Unmounting Real ID flow");
        unmountAllFlows();
        flowLoaded = false;
        console.log("[CF] Flow unmounted, ready for reinitialization");
    };
    // Helper function to load Real ID flow when mount point is found
    const loadFlowIfMountExists = () => {
        if (flowLoaded) {
            console.log("[CF] Flow already loaded, skipping");
            return true;
        }
        const target = document.querySelector("#real-id-mount");
        if (target) {
            flowLoaded = true;
            console.log("Real ID mount detected, loading Real ID flow");
            // Extract Customer Fields data before creating flow
            extractCFData((cfData) => {
                if (cfData) {
                    console.log("[CF] Passing customer data to Real ID flow");
                    createFlow({
                        target: "#real-id-mount",
                        mode: "full",
                        email: cfData.email,
                        firstName: cfData.firstName,
                        lastName: cfData.lastName,
                        phone: cfData.phone,
                    });
                }
                else {
                    console.log("[CF] Creating Real ID flow without customer data");
                    createFlow({
                        target: "#real-id-mount",
                        mode: "full",
                    });
                }
            });
            // hide the target element, since the SDK makes a sibling element
            target.style.display = "none";
            return true;
        }
        return false;
    };
    // Setup mount point observer - continuously watches for #real-id-mount
    const setupMountPointObserver = ($formElement) => {
        console.log("[CF] Setting up MutationObserver to watch for #real-id-mount appearing");
        const mountObserver = new MutationObserver(() => {
            if (!flowLoaded) {
                const found = loadFlowIfMountExists();
                if (found) {
                    console.log("[CF] Mount point detected via MutationObserver, disconnecting observer");
                    mountObserver.disconnect();
                }
            }
        });
        mountObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
        console.log("[CF] MutationObserver active for #real-id-mount detection");
        // Strategy 3: Listen for Customer Fields button clicks
        console.log("[CF] Setting up delegated click listener for .cf-button elements");
        const buttonClickHandler = (event) => {
            const target = event.target;
            const button = target.closest('.cf-button');
            if (button) {
                console.log("[CF] Customer Fields navigation button clicked, checking mount presence");
                // Small delay to allow Customer Fields to update DOM
                setTimeout(() => {
                    const mountExists = mountExistsInDOM();
                    if (mountExists && !flowLoaded) {
                        console.log("[CF] Mount exists in DOM and flow not loaded - initializing");
                        loadFlowIfMountExists();
                    }
                    else if (!mountExists && flowLoaded && !isFlowActive()) {
                        console.log("[CF] Mount removed and flow inactive - unmounting");
                        unmountFlow();
                    }
                }, 100);
            }
        };
        // Use event delegation to catch clicks on dynamically added buttons
        $formElement.addEventListener('click', buttonClickHandler);
        console.log("[CF] Button click listener registered");
        // Keep monitoring with MutationObserver + button clicks + events
        // Note: MutationObserver runs indefinitely to support customers who take any
        // amount of time to complete the form. It auto-disconnects when mount point
        // is found or when the page unloads. Performance cost is negligible (~0ms CPU, <5KB memory).
    };
    const $form = document.querySelector(".cf-react-target");
    if ($form) {
        const foundTime = performance.now() - startTime;
        console.log(`Customer Fields Form found (found in ${foundTime.toFixed(0)}ms)`);
        console.log(`[CF] Looking for mount point: #real-id-mount`);
        // Check if mount point already exists
        if (loadFlowIfMountExists()) {
            // Mount point already exists, nothing more to do
            return;
        }
        // Mount point doesn't exist yet, set up various detection methods
        console.log("[CF] Mount point not found yet, setting up detection strategies");
        // Strategy 1: Listen for Customer Fields custom events (form-level)
        const cfEvents = ["cf:step_changed", "cf:page_changed", "cf:form_updated"];
        cfEvents.forEach(eventName => {
            console.log(`[CF] Registering listener for event: ${eventName}`);
            $form.addEventListener(eventName, function (event) {
                console.log(`[CF] Event fired: ${eventName}, checking mount presence`);
                const mountExists = mountExistsInDOM();
                if (mountExists && !flowLoaded) {
                    console.log("[CF] Mount exists in DOM and flow not loaded - initializing");
                    loadFlowIfMountExists();
                }
                else if (!mountExists && flowLoaded && !isFlowActive()) {
                    console.log("[CF] Mount removed and flow inactive - unmounting");
                    unmountFlow();
                }
            });
        });
        // Strategy 2, 3: MutationObserver + Button Clicks (reliable detection)
        setupMountPointObserver($form);
    }
    else {
        console.log("[CF] Form not found, setting up MutationObserver to retry...");
        let retryCount = 0;
        const maxRetries = 20; // 10 seconds worth of retries
        const retryInterval = 500; // ms
        const observer = new MutationObserver(() => {
            retryCount++;
            const retryTime = performance.now() - startTime;
            console.log(`[CF] Retry attempt ${retryCount} (${retryTime.toFixed(0)}ms elapsed) - checking for .cf-react-target`);
            const $retryForm = document.querySelector(".cf-react-target");
            if ($retryForm) {
                console.log(`Customer Fields Form found (found after ${retryTime.toFixed(0)}ms)`);
                console.log(`[CF] Looking for mount point: #real-id-mount`);
                observer.disconnect();
                // Check if mount point already exists
                if (loadFlowIfMountExists()) {
                    return;
                }
                // Mount point doesn't exist yet, set up detection
                console.log("[CF] Mount point not found yet, setting up detection strategies");
                // Strategy 1: Listen for Customer Fields custom events
                const cfEvents = ["cf:step_changed", "cf:page_changed", "cf:form_updated"];
                cfEvents.forEach(eventName => {
                    console.log(`[CF] Registering listener for event: ${eventName}`);
                    $retryForm.addEventListener(eventName, function (event) {
                        console.log(`[CF] Event fired: ${eventName}, checking mount presence`);
                        const mountExists = mountExistsInDOM();
                        if (mountExists && !flowLoaded) {
                            console.log("[CF] Mount exists in DOM and flow not loaded - initializing");
                            loadFlowIfMountExists();
                        }
                        else if (!mountExists && flowLoaded && !isFlowActive()) {
                            console.log("[CF] Mount removed and flow inactive - unmounting");
                            unmountFlow();
                        }
                    });
                });
                // Strategy 2: MutationObserver (reliable detection)
                setupMountPointObserver($retryForm);
            }
            else if (retryCount >= maxRetries) {
                const timeoutTime = performance.now() - startTime;
                console.log(`[CF] Giving up after ${retryCount} retries (${timeoutTime.toFixed(0)}ms) - Customer Fields form never appeared`);
                observer.disconnect();
            }
        });
        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        console.log(`[CF] Observer started, will retry every ${retryInterval}ms for ${maxRetries} attempts`);
        // Also use interval as backup in case mutations don't fire
        let intervalId = setInterval(() => {
            if (retryCount >= maxRetries || document.querySelector(".cf-react-target")) {
                if (intervalId)
                    clearInterval(intervalId);
            }
        }, retryInterval);
    }
}
// Only run CustomerFields integration on Shopify
// CustomerFields is a Shopify-exclusive app - no need to poll/observe on WC or Hosted
(function () {
    const platform = detectCurrentPlatform();
    if (platform === "shopify") {
        customCFIntegration();
    }
})();
/**
 * Handle unverified customers on WC that manage to get to the checkout validation screen
 *
 * This will create a new Real ID Flow mount just below the error messages in the checkout page (shortcode version)
 */
if (window.jQuery) {
    console.log("Registering WC checkout error validation handler");
    window
        .jQuery(document.body)
        .on("checkout_error", function (event, errorMessage) {
        console.log("Checkout error:", errorMessage);
        // if the error message is that ID verification is required, then show the ID verification prompt
        if (errorMessage.includes("ID verification is required to checkout")) {
            // then mount the ID verification button just below the error message for visibility
            // Select the element with the class 'woocommerce-NoticeGroup-checkout'
            // const noticeGroup = document.querySelector(
            // ".woocommerce-NoticeGroup-checkout"
            // ".woocommerce-notices-wrapper"
            // );
            // Select all elements with the class '.woocommerce-notices-wrapper'
            const noticeGroups = document.querySelectorAll(".woocommerce-notices-wrapper");
            // Get the last element from the NodeList
            const noticeGroup = noticeGroups[noticeGroups.length - 1];
            if (noticeGroup) {
                // adding a half second delay just in case
                setTimeout(() => {
                    console.log("Creating WC errors mount");
                    // Create a new <div> with the class 'verify-id-prompt'
                    const newDiv = document.createElement("div");
                    newDiv.className = "verify-id-prompt";
                    newDiv.id = "wc-errors-real-id-prompt";
                    // Insert the new div as the last child within the selected element, so that way it appears immediately after the checkbox
                    noticeGroup.appendChild(newDiv);
                    console.log("Re-intializing");
                    initialize();
                }, 500);
            }
        }
    });
}
